import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import type { RouteOption, RouteOptionPayload } from '@/types/domain'

/* Route options (Story 2.4, FR-3).
 *
 * Beside useQuote.ts rather than inside it: the cost-line hooks there and
 * these serve different stories, and the file split mirrors the backend's
 * routeoption_*.go seam.
 *
 * Nothing here is optimistic. Options are added a couple of times per quote,
 * not fifty times like cost lines — and two of the writes below are exactly
 * the operations the server may REFUSE (the fourth option, the unacknowledged
 * selection move), so rendering them as done first would show the state the
 * refusal exists to prevent.
 *
 * Every success invalidates the DETAIL, because the options live on it: the
 * server owns the selection invariant (a delete may silently move the mark to
 * another option), so refetching the set is the only way to render what is
 * true rather than what this client can guess. The list is refreshed too —
 * the pipeline card's lane and mode come off the selected option.
 */

function useInvalidateQuote(quoteId: string) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.detail(quoteId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.list() })
  }
}

export function useCreateRouteOption(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<RouteOption, Error, RouteOptionPayload>({
    mutationFn: (payload) =>
      api.post<RouteOption>(`/quotes/${quoteId}/route-options`, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateRouteOption(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<RouteOption, Error, { optionId: string; payload: RouteOptionPayload }>({
    // A whole-record PUT, like the header's: a partial body would make "clear
    // the indicative total" indistinguishable from "leave it alone".
    mutationFn: ({ optionId, payload }) =>
      api.put<RouteOption>(`/quotes/${quoteId}/route-options/${optionId}`, payload),
    onSuccess: invalidate,
  })
}

/** Moves the selection (FR-3). Retried with `acknowledged: true` after the
 *  user confirms the repricing warning — see needsAcknowledgement. */
export function useSelectRouteOption(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<void, Error, { optionId: string; acknowledged?: boolean }>({
    mutationFn: ({ optionId, acknowledged }) =>
      api.post<void>(
        `/quotes/${quoteId}/route-options/${optionId}/select`,
        acknowledged ? { acknowledgeRepricing: true } : {},
      ),
    onSuccess: invalidate,
  })
}

export function useDeleteRouteOption(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<void, Error, string>({
    mutationFn: (optionId) =>
      api.delete<void>(`/quotes/${quoteId}/route-options/${optionId}`),
    onSuccess: invalidate,
  })
}

/* The three FR-3 refusals, recognised by the token the server puts in the
 * problem detail (handlers.go fail). Predicates here rather than string
 * comparisons in components, so the wire token is spelled in exactly one
 * place per side. */

/** The selection move needs the repricing confirmation first. The ORDINARY
 *  first half of the confirm flow, not a failure to report. */
export function needsAcknowledgement(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.problem.detail === 'selection-needs-acknowledgement'
  )
}

/** The selected option holds the costing and cannot be deleted (FR-3). */
export function optionHoldsCosting(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.problem.detail === 'selected-option-holds-costing'
  )
}

/** The quote already offers three routings (FR-3). */
export function optionsCapped(error: unknown): boolean {
  return (
    error instanceof ApiError && error.problem.detail === 'route-options-capped'
  )
}
